import type { CredentialStatus, SupplierCredential, SupplierCredentialInput } from '../types'
import { encryptionService } from '../services/encryption-service'
import { readJson, STORAGE_KEYS, writeJson } from './storage'

/**
 * SupplierCredentialRepository — stores only encrypted secrets.
 */
export class SupplierCredentialRepository {
  list(): SupplierCredential[] {
    return readJson<SupplierCredential[]>(STORAGE_KEYS.credentials, 'credentials', [])
  }

  findBySupplierId(supplierId: string): SupplierCredential | null {
    return this.list().find((c) => c.supplierId === supplierId) ?? null
  }

  findById(id: string): SupplierCredential | null {
    return this.list().find((c) => c.id === id) ?? null
  }

  async upsert(supplierId: string, input: SupplierCredentialInput): Promise<SupplierCredential> {
    const existing = this.findBySupplierId(supplierId)
    const now = new Date().toISOString()

    const encryptedPassword =
      input.password != null && input.password !== ''
        ? await encryptionService.encrypt(input.password)
        : existing?.encryptedPassword ?? null

    const apiKey =
      input.apiKey != null && input.apiKey !== ''
        ? encryptionService.isEncrypted(input.apiKey)
          ? input.apiKey
          : await encryptionService.encrypt(input.apiKey)
        : existing?.apiKey ?? null

    const apiSecret =
      input.apiSecret != null && input.apiSecret !== ''
        ? encryptionService.isEncrypted(input.apiSecret)
          ? input.apiSecret
          : await encryptionService.encrypt(input.apiSecret)
        : existing?.apiSecret ?? null

    const token =
      input.token != null && input.token !== ''
        ? encryptionService.isEncrypted(input.token)
          ? input.token
          : await encryptionService.encrypt(input.token)
        : existing?.token ?? null

    const refreshToken =
      input.refreshToken != null && input.refreshToken !== ''
        ? encryptionService.isEncrypted(input.refreshToken)
          ? input.refreshToken
          : await encryptionService.encrypt(input.refreshToken)
        : existing?.refreshToken ?? null

    if (existing) {
      const next: SupplierCredential = {
        ...existing,
        username: input.username === undefined ? existing.username : input.username,
        encryptedPassword,
        apiKey,
        apiSecret,
        token,
        refreshToken,
        expiresAt: input.expiresAt === undefined ? existing.expiresAt : input.expiresAt,
        updatedAt: now,
      }
      this.replace(next)
      return next
    }

    const created: SupplierCredential = {
      id: crypto.randomUUID(),
      supplierId,
      username: input.username ?? null,
      encryptedPassword,
      apiKey,
      apiSecret,
      token,
      refreshToken,
      expiresAt: input.expiresAt ?? null,
      lastLogin: null,
      status: 'DISCONNECTED',
      createdAt: now,
      updatedAt: now,
    }
    writeJson(STORAGE_KEYS.credentials, 'credentials', [created, ...this.list()])
    return created
  }

  updateStatus(supplierId: string, status: CredentialStatus, lastLogin?: string | null): SupplierCredential {
    const existing = this.findBySupplierId(supplierId)
    if (!existing) throw new Error('Credencial não encontrada')
    const next: SupplierCredential = {
      ...existing,
      status,
      lastLogin: lastLogin === undefined ? existing.lastLogin : lastLogin,
      updatedAt: new Date().toISOString(),
    }
    this.replace(next)
    return next
  }

  removeBySupplierId(supplierId: string): void {
    writeJson(
      STORAGE_KEYS.credentials,
      'credentials',
      this.list().filter((c) => c.supplierId !== supplierId),
    )
  }

  private replace(next: SupplierCredential): void {
    writeJson(
      STORAGE_KEYS.credentials,
      'credentials',
      this.list().map((c) => (c.id === next.id ? next : c)),
    )
  }
}

export const supplierCredentialRepository = new SupplierCredentialRepository()
